import { format as formatFn } from 'date-fns';
import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

type TimeAgoProps = {
  time: string | number | Date | null | undefined;
  thresholdInMinutes?: number;
  format?: string;
  isShowTimeElapses?: boolean;
};

const MS_PER_MINUTE = 60 * 1000;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_DAY = 24 * MS_PER_HOUR;
const MS_PER_MONTH = 30 * MS_PER_DAY;
const MS_PER_YEAR = 365 * MS_PER_DAY;

type RelativeTimeResult = {
  key: string;
  count?: number;
};

const getRelativeTime = (dateStr: string): RelativeTimeResult => {
  const now = Date.now();
  const date = new Date(dateStr).getTime();
  const diffMs = now - date;

  if (diffMs < 0) return { key: 'just_now' };

  const minutes = Math.floor(diffMs / MS_PER_MINUTE);
  const hours = Math.floor(diffMs / MS_PER_HOUR);
  const days = Math.floor(diffMs / MS_PER_DAY);
  const months = Math.floor(diffMs / MS_PER_MONTH);
  const years = Math.floor(diffMs / MS_PER_YEAR);

  if (minutes < 1) return { key: 'just_now' };
  if (minutes === 1) return { key: 'minute_one' };
  if (minutes < 60) return { key: 'minutes', count: minutes };
  if (hours === 1) return { key: 'hour_one' };
  if (hours < 24) return { key: 'hours', count: hours };
  if (days === 1) return { key: 'day_one' };
  if (days < 30) return { key: 'days', count: days };
  if (months === 1) return { key: 'month_one' };
  if (months < 12) return { key: 'months', count: months };
  if (years === 1) return { key: 'year_one' };
  return { key: 'years', count: years };
};

const TimeAgo: FunctionComponent<TimeAgoProps> = ({ time, format = 'yyyy-MM-dd HH:mm', isShowTimeElapses = false }) => {
  const { t } = useTranslation('time_elapsed');

  if (isShowTimeElapses) {
    const { key, count } = getRelativeTime(time as string);
    return <span className='text-xs text-gray-500'>{t(`${key}`, { count }) as string}</span>;
  }

  if (time) {
    if (typeof time === 'string' || typeof time === 'number') {
      try {
        time = new Date(time);
      } catch (e) {
        return null;
      }
    }
    return <>{formatFn(time, format)}</>;
  }
  return <></>;
};

export default TimeAgo;
